import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
	ShoppingBag,
	Calendar,
	Ruler,
	Package,
	AlertCircle,
} from "lucide-react";
import moment from "moment";
import { getOrderDetails } from "../actions/orderActions";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Toast } from "./customize/Toast";

const TableHeader = ({ children, className }) => (
	<th className={`px-4 py-3 text-sm font-medium text-gray-600 ${className}`}>
		{children}
	</th>
);

const TableCell = ({ children, className }) => (
	<td className={`px-4 py-4 text-sm text-gray-700 ${className}`}>{children}</td>
);

const MyOrderScreen = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const userLogin = useSelector((state) => state.userLogin);
	const { userInfo } = userLogin;

	const orderDetails = useSelector((state) => state.orderDetails);
	const { loading, error, orders } = orderDetails;

	useEffect(() => {
		if (!userInfo) {
			navigate("/login");
		} else {
			dispatch(getOrderDetails(userInfo, navigate));
		}
	}, [navigate, dispatch, userInfo]);

	const floatToFraction = (decimal) => {
		const gcd = (a, b) => (!b ? a : gcd(b, a % b));

		if (decimal.toString().includes(".")) {
			const [whole, fraction] = decimal.toString().split(".");
			const numerator = parseFloat(`0.${fraction}`) * 100;
			const denominator = 100;
			const div = gcd(numerator, denominator);

			return `${whole} ${(numerator / div).toString()}/${(
				denominator / div
			).toString()}`;
		}
		return decimal;
	};

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="flex items-center gap-3 mb-8">
					<ShoppingBag className="w-8 h-8 text-primary" />
					<h1 className="text-2xl font-bold">My Orders</h1>
				</div>

				{/* Error Message */}
				<Toast show={error} type="error" message={error} />

				{/* Orders Table */}
				<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
					<div className="overflow-x-auto">
						<table className="w-full">
							<thead className="bg-gray-50 border-b border-gray-200">
								<tr>
									<TableHeader>Quantity</TableHeader>
									<TableHeader>Date</TableHeader>
									<TableHeader>Shade</TableHeader>
									<TableHeader>Group</TableHeader>
									<TableHeader>Dimensions</TableHeader>
									<TableHeader>Control</TableHeader>
									<TableHeader>Valance</TableHeader>
									<TableHeader>Hem</TableHeader>
									<TableHeader>Measure</TableHeader>
								</tr>
							</thead>
							<tbody className="divide-y divide-gray-200">
								{Array.isArray(orders) &&
									orders
										?.sort((a, b) => new Date(b.date) - new Date(a.date))
										?.map((order, index) => (
											<motion.tr
												key={index}
												initial={{ opacity: 0, y: 20 }}
												animate={{ opacity: 1, y: 0 }}
												transition={{ delay: index * 0.1 }}
												className="hover:bg-gray-50"
											>
												<TableCell className="text-center font-medium">
													{order.quantity}
												</TableCell>
												<TableCell className="whitespace-nowrap">
													<div className="flex items-center gap-2">
														<Calendar className="w-4 h-4 text-gray-400" />
														{moment(order.date).format("MM-DD-YYYY")}
													</div>
												</TableCell>
												<TableCell>{order.shade}</TableCell>
												<TableCell>{order.group}</TableCell>
												<TableCell className="whitespace-nowrap">
													<div className="flex items-center gap-2">
														<Ruler className="w-4 h-4 text-gray-400" />
														{floatToFraction(order.width)} x{" "}
														{floatToFraction(order.length)}
													</div>
												</TableCell>
												<TableCell>{order.controlSystem}</TableCell>
												<TableCell>{order.valance}</TableCell>
												<TableCell>{order.hem}</TableCell>
												<TableCell>{order.measure}</TableCell>
											</motion.tr>
										))}
							</tbody>
						</table>
					</div>

					{/* Empty State */}
					{orders?.length === 0 && (
						<div className="p-8 text-center">
							<Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
							<h3 className="text-lg font-medium text-gray-900 mb-1">
								No orders yet
							</h3>
							<p className="text-gray-500">
								Start shopping to see your orders here.
							</p>
						</div>
					)}
				</div>
			</div>

			{/* Loading Overlay */}
			<AnimatePresence>
				{loading && (
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
					>
						<LoadingSpinner text="Loading your orders..." />
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

export default MyOrderScreen;
